import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTrendingMovies, getTrendingTvShows } from '../services/tmdbService';
import { getRecentlyViewed, clearRecentlyViewed } from '../utils/recentlyViewed';
import '../styles/Home.css';

const ContentCard = ({ item }) => {
  const mediaType = item.media_type || item.type || 'movie';
  const title = item.title || item.name;
  const linkTo = mediaType === 'movie' ? `/movie/${item.id}` : `/tv/${item.id}`;
  const posterUrl = item.poster_path
    ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
    : null;

  return (
    <div className="content-card">
      <Link to={linkTo} aria-label={title}>
        <div className="card-poster">
          {posterUrl ? (
            <img src={posterUrl} alt={title} loading="lazy" />
          ) : (
            <div className="card-poster-fallback">{title}</div>
          )}
          <div className="card-overlay">
            <p className="card-title">{title}</p>
            {typeof item.vote_average === 'number' && item.vote_average > 0 && (
              <span className="card-rating">
                <i className="fa-solid fa-star" aria-hidden="true"></i>
                {item.vote_average.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

const Home = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [content, setContent] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setRecentlyViewed(getRecentlyViewed());
  }, []);

  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    if (trimmed) {
      navigate(`/search?search=${encodeURIComponent(trimmed)}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'all') {
        const [movies, tv] = await Promise.all([
          getTrendingMovies(1),
          getTrendingTvShows(1),
        ]);
        const combined = [];
        const max = Math.max(movies.length, tv.length);
        for (let i = 0; i < max; i++) {
          if (movies[i]) combined.push({ ...movies[i], media_type: 'movie' });
          if (tv[i]) combined.push({ ...tv[i], media_type: 'tv' });
        }
        setContent(combined);
      } else if (activeTab === 'movies') {
        const movies = await getTrendingMovies(1);
        setContent(movies.map((m) => ({ ...m, media_type: 'movie' })));
      } else {
        const tv = await getTrendingTvShows(1);
        setContent(tv.map((t) => ({ ...t, media_type: 'tv' })));
      }
    } catch {
      setError('Couldn’t load content. Please try again.');
      setContent([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleClearHistory = () => {
    clearRecentlyViewed();
    setRecentlyViewed([]);
  };

  return (
    <div className="home">
      <section className="hero-search">
        <h1>What do you want to watch?</h1>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search movies and TV shows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button type="button" onClick={handleSearch} aria-label="Search">
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>
      </section>

      <section className="browse-tabs" role="tablist">
        {['all', 'movies', 'tvshows'].map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'all' ? 'All' : tab === 'movies' ? 'Movies' : 'TV Shows'}
          </button>
        ))}
      </section>

      {recentlyViewed.length > 0 && (
        <section className="recently-viewed">
          <div className="section-header">
            <h2>Continue Watching</h2>
            <button
              type="button"
              className="clear-history"
              onClick={handleClearHistory}
            >
              Clear history
            </button>
          </div>
          <div className="content-grid">
            {recentlyViewed.slice(0, 6).map((item) => (
              <ContentCard key={`${item.type}-${item.id}`} item={item} />
            ))}
          </div>
        </section>
      )}

      <section className="browse-content">
        <h2>
          {activeTab === 'all'
            ? 'Trending Now'
            : activeTab === 'movies'
            ? 'Trending Movies'
            : 'Trending TV Shows'}
        </h2>
        {loading ? (
          <div className="loading">Loading</div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button type="button" onClick={fetchContent}>Try again</button>
          </div>
        ) : content.length === 0 ? (
          <div className="empty-state">Nothing to show right now.</div>
        ) : (
          <div className="content-grid">
            {content.map((item, index) => (
              <ContentCard
                key={`${item.media_type || activeTab}-${item.id}-${index}`}
                item={item}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
